import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { ScrEnvironment } from 'src/environments/environment';
import { ScrAuthenticationService, WITHCRED } from './authentication.service';
import { IScrBaseResponse, ScrBaseService, ScrMessage } from './base.service';
import { ConfigService } from './config.service';
import { ScrServiceManager } from './servicemanager.service';
import { NGXLogger } from 'ngx-logger';


interface MsSessionStateResponse extends IScrBaseResponse {
  sessionstate: string;
  url: string;
  keepAliveUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class MicrostrategyService extends ScrBaseService {

  // ID token we retrieve with an SCR back-end call.
  // Session ID we get by trading ID token for a session.

  private keepAliveInterval = -1;
  private customerSupportPhone?: string;

  constructor(private environment: ScrEnvironment,
    sm: ScrServiceManager,
    private logger: NGXLogger,
    private cs: ConfigService,
    private as: ScrAuthenticationService,
    private http: HttpClient) {
    super(sm);

    this.cs.getConfig().pipe(first()).subscribe(cfg => {
      this.customerSupportPhone = cfg.customerSupportPhone;
    });

    // one open issue here.  If user signs into BI and we have a session
    // but they press reload without relaunching the BI, we will not
    // be running keep-alive requests.  They will have to keep active in BI.
  }

  async launchMicrostrategyBi(): Promise<ScrMessage[] | undefined> {
    const as = this.as.getAuthState();
    if (!as) {
      console.error('how could we be asked to launch BI with the user not logged in');
      const msg = this.translate.instant("scr.error.5100", { 'sdPhone': this.customerSupportPhone }) as string;
      return Promise.resolve([new ScrMessage('5100', msg)]);
    }
    const msIdToken = as.msBiState?.idToken;
    if (!msIdToken) {
      console.error('we should have received this during login');
    }

    // if already have session, use it.
    if (as.msBiState?.launchUrl) {
      window.open(as.msBiState?.launchUrl, '_blank');
      // make sure keep alive is running.  If user reloads page
      // we may have the launchUrl from before but the interval is gone.
      this.startKeepAlive();
      return Promise.resolve(undefined);
    }

    // 1) trade the ID token for a session token.
    // 2) open a browser window using that data.
    const url = `${this.environment.base}/secure/bi/microstrategyid2ss`;
    const body = {
      msIdToken,
      accessToken: await this.as.getAccessToken()
    };

    return firstValueFrom(this.http.post<MsSessionStateResponse>(url, body, WITHCRED)
      .pipe(map(rsp => {
        if ((rsp.status === 'success') && (rsp.sessionstate)) {
          const newas = this.as.getAuthState();
          if (newas?.msBiState) {
            newas.msBiState.sessionState = rsp.sessionstate;
            newas.msBiState.launchUrl = rsp.url;
            newas.msBiState.keepAliveUrl = rsp.keepAliveUrl;
          }
          window.open(rsp.url, '_blank');

          // initiate keep-alive process.  kill the old interval if running.
          this.startKeepAlive();
          return undefined;
        }
        else if (rsp.messages && rsp.messages.length > 0) {
          return rsp.messages;
        }
        return undefined;
      })));
  }

  private startKeepAlive() {

    if (this.keepAliveInterval > 0) {
      window.clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = -1;
    }

    this.keepAliveInterval = window.setInterval(() => {
      if (!this.as.isUserSignedIn()) {
        // user no longer signed in.  cancel the interval.
        window.clearInterval(this.keepAliveInterval);
        this.keepAliveInterval = -1;
      }
      else {
        const as = this.as.getAuthState();
        if (!as || !as.msBiState) {
          console.info('attempting to keep session alive but no auth state available.');
          window.clearInterval(this.keepAliveInterval);
          this.keepAliveInterval = -1;
        }
        else {
          if (!as.msBiState.keepAliveUrl) {
            console.info('ms bi keep alive url is not set');
            window.clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = -1;
            }
          else {
            this.logger.trace('do keep alive ' + as.msBiState.keepAliveUrl);
            this.http.get<string>(as.msBiState.keepAliveUrl).subscribe(rsp => {
              this.logger.trace('keep alive returned ' + rsp);
            });
          }
        }
      }
    }, 120_000);
  }
}

